import { useState, useEffect, useCallback } from 'react';
import { AnnotationType, AnnotationStatus } from '../../plan/models/annotation.model';

const DEFAULT_STATUSES: AnnotationStatus[] = ['TODO', 'IN_PROGRESS', 'NEW', 'PENDING'];

export const useAnnotationFilters = (onClose: () => void, onActiveFiltersCountChange?: (count: number) => void, hideSiteFilter?: boolean, availableAuthors?: string[], availablePortfolios?: string[], availableSitesFromAnnotations?: { id: string; name: string }[]) => {
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<AnnotationType[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<AnnotationStatus[]>(DEFAULT_STATUSES);
  const [selectedAuthors, setSelectedAuthors] = useState<string[]>([]);
  const [selectedPortfolios, setSelectedPortfolios] = useState<string[]>([]);
  const [hasAttachment, setHasAttachment] = useState<string | null>(null);
  const [availableSites, setAvailableSites] = useState<{ id: string; name: string }[]>([]);

  const countActiveFilters = useCallback((): number => {
    let count = 0;
    
    // Compter les sites sélectionnés seulement si le filtre n'est pas masqué
    if (!hideSiteFilter && selectedSites.length > 0) {
      count += selectedSites.length;
    }
    
    // Compter les types sélectionnés
    if (selectedTypes.length > 0) {
      count += selectedTypes.length;
    }
    
    // Compter les statuts sélectionnés (ils représentent des filtres actifs)
    if (selectedStatuses.length > 0) {
      count += selectedStatuses.length;
    }

    // Compter les auteurs sélectionnés
    if (selectedAuthors.length > 0) {
      count += selectedAuthors.length;
    }

    // Compter les portfolios sélectionnés
    if (selectedPortfolios.length > 0) {
      count += selectedPortfolios.length;
    }

    // Compter les filtres d'attachement
    if (hasAttachment !== null) {
      count += 1;
    }
    
    return count;
  }, [selectedSites, selectedTypes, selectedStatuses, hasAttachment, selectedAuthors, selectedPortfolios, hideSiteFilter]);

  const getSitesCount = (): number => {
    return selectedSites.length;
  };

  const getTypesCount = (): number => {
    return selectedTypes.length;
  };

  const getStatusesCount = (): number => {
    return selectedStatuses.length;
  };

  const getAuthorsCount = (): number => {
    return selectedAuthors.length;
  };

  const getPortfoliosCount = (): number => {
    return selectedPortfolios.length;
  };

  const getAttachmentCount = (): number => {
    return hasAttachment !== null ? 1 : 0;
  };

  // Utiliser les sites extraits des annotations chargées
  useEffect(() => {
    if (availableSitesFromAnnotations) {
      setAvailableSites(availableSitesFromAnnotations);
    } else {
      setAvailableSites([]);
    }
  }, [availableSitesFromAnnotations]);

  // Appeler la fonction de comptage quand les filtres changent
  useEffect(() => {
    if (onActiveFiltersCountChange) {
      onActiveFiltersCountChange(countActiveFilters());
    }
  }, [selectedSites, selectedTypes, selectedStatuses, hasAttachment, selectedAuthors, selectedPortfolios, onActiveFiltersCountChange, countActiveFilters]);

  return {
    selectedSites,
    setSelectedSites,
    selectedTypes,
    setSelectedTypes,
    selectedStatuses,
    setSelectedStatuses,
    selectedAuthors,
    setSelectedAuthors,
    selectedPortfolios,
    setSelectedPortfolios,
    hasAttachment,
    setHasAttachment,
    availableSites,
    availableAuthors: availableAuthors || [],
    availablePortfolios: availablePortfolios || [],
    countActiveFilters,
    getSitesCount,
    getTypesCount,
    getStatusesCount,
    getAuthorsCount,
    getPortfoliosCount,
    getAttachmentCount
  };
}; 
